import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { FONTSIZE, sizes, theme, breakpoints } from '../theme';

// ------------ Button Elements ------------

export const TertiaryButton = styled.button`
    display: block;
    cursor: pointer;
    font-size: ${FONTSIZE.BodyRegular};
    font-family: ${theme.fontFamilyMain};
    text-decoration: underline;
    letter-spacing: 0;
    border: none;
    background-color: transparent;
    color: ${theme.textColour};
    &:hover {
        background-color: ${theme.secondaryButtonHoverBackgroundColour};
        border-radius: 4px;
    }
    &:focus {
        background-color: ${theme.buttonFocusBackgroundColour};
        border: 1px solid ${theme.primaryButtonBorderColour};
        outline: ${theme.primaryButtonBorderColour};
    }
`;

export const ExternalSecondaryLinkButton = styled.a`
    font-size: ${FONTSIZE.BodyRegular};
    text-decoration: underline;
    letter-spacing: 0;
    font-weight: bold;
    color: ${theme.linkColour};
    &:hover {
        background-color: ${theme.linkHoverBackgroundColour};
        border-radius: 4px;
    }
    &:visited {
        color: ${theme.linkVisitedColour};
    }
    &:focus {
        background-color: ${theme.linkFocusBackgroundColour};
        border: 1px solid ${theme.linkFocusBorderColour};
        outline: ${theme.linkFocusBorderColour};
        border-radius: 4px;
    }
`;

export const NavigationLink = styled(Link)<{ $inverted?: boolean }>`
    display: flex;
    align-items: center;
    gap: ${sizes.spacingXs};
    color: ${({ $inverted }) => {
        return $inverted ? theme.textColourInverted : theme.textColour;
    }};
    font-size: ${FONTSIZE.BodyRegular};
    text-decoration: underline;
    font-weight: bold;
    &:focus {
        background-color: ${({ $inverted }) => {
            return $inverted ? theme.primaryBrandColour : theme.linkFocusBackgroundColour;
        }};
        border: 1px solid ${theme.linkFocusBorderColour};
        border-radius: ${sizes.borderRadiusMd};
        outline: none;
    }
    &:hover {
        background-color: ${({ $inverted }) => {
            return $inverted ? theme.primaryBrandColour : theme.linkHoverBackgroundColour;
        }};
        border-radius: ${sizes.borderRadiusMd};
    }
`;

export const NavLinkNoWrap = styled(NavigationLink)`
    white-space: nowrap;
`;

export const IconLinkButtonText = styled.div<{ inverted?: boolean }>`
    font-size: ${FONTSIZE.BodyRegular};
    text-decoration: underline;
    font-weight: bold;
    white-space: nowrap;
    color: ${({ inverted }) => {
        return inverted ? theme.textColourInverted : theme.linkColour;
    }};
`;

// ------------ Text Elements ------------

const HeadlineBase = styled.h1<{ $inverted?: boolean }>`
    font-weight: 600;
    font-family: ${theme.fontFamilyHeader};
    padding: 0;
    margin: 0;
    overflow-wrap: break-word;
    overflow: hidden;

    color: ${({ $inverted }) => {
        return $inverted ? theme.textColourInverted : theme.textColour;
    }};
`;

export const Headline1 = styled(HeadlineBase).attrs({ as: 'h1' })<{ $inverted?: boolean }>`
    font-size: ${FONTSIZE.Headline1};
`;

export const Headline2 = styled(HeadlineBase).attrs({ as: 'h2' })<{ $inverted?: boolean }>`
    font-size: ${FONTSIZE.Headline2};
`;

export const Headline3 = styled(HeadlineBase).attrs({ as: 'h3' })<{ $inverted?: boolean }>`
    font-size: ${FONTSIZE.Headline3};
`;

export const Headline4 = styled(HeadlineBase).attrs({ as: 'h4' })<{ $inverted?: boolean }>`
    font-size: ${FONTSIZE.Headline4};
`;

export const Headline5 = styled(HeadlineBase).attrs({ as: 'h5' })<{ $inverted?: boolean }>`
    font-size: ${FONTSIZE.Headline5};
`;

export const Headline6 = styled(HeadlineBase).attrs({ as: 'h6' })<{ $inverted?: boolean }>`
    font-size: ${FONTSIZE.Headline6};
`;

const BodyBase = styled.p<{ $inverted?: boolean; $bold?: boolean; $nowrap?: boolean }>`
    font-family: ${theme.fontFamilyMain};
    padding: 0;
    margin: 0;
    overflow-wrap: break-word;
    word-break: break-word;
    white-space: ${({ $nowrap }) => ($nowrap ? 'nowrap' : 'pre-wrap')};
    font-weight: ${({ $bold }) => ($bold ? 600 : 'normal')};
    color: ${({ $inverted }) => {
        return $inverted ? theme.textColourInverted : theme.textColour;
    }};
`;

export const BodyLarge = styled(BodyBase)<{ $inverted?: boolean; $bold?: boolean }>`
    font-size: ${FONTSIZE.BodyLarge};
`;

export const BodyRegular = styled(BodyBase)<{ $inverted?: boolean; $bold?: boolean; $nowrap?: boolean }>`
    font-size: ${FONTSIZE.BodyRegular};
`;

export const BodySmall = styled(BodyBase)<{ $inverted?: boolean; $bold?: boolean }>`
    font-size: ${FONTSIZE.BodySmall};
`;

export const WrappingText = styled(BodyRegular)`
    text-overflow: ellipsis;
    overflow: hidden;
    word-wrap: anywhere;
`;

export const BoldSpan = styled.span`
    font-weight: bold;
`;

export const BulletList = styled.ul`
    list-style-position: inside;
`;

export const NoBulletList = styled.ul`
    list-style: none;
`;

export const OrderedList = styled.ol`
    list-style-position: inside;
`;

// ------------ ------------

export const CardBase = css`
    padding: 20px;
    border: 1px solid ${theme.cardBorder};
    background: ${theme.cardBackground};
    box-shadow: 2px 4px 6px 0 rgba(0, 0, 0, 0.1);
    border-radius: ${sizes.borderRadiusLg};
    box-sizing: border-box;
`;

export const Card = styled.div`
    ${CardBase}
`;

// --- MODALS ---
export const ModalBackground = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: 9999;
`;

export const ModalBody = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

// ------------ CSS Utilities ------------

//FLEX ROWS
export const Flex = styled.div`
    display: flex;
`;

export const FlexEnd = styled(Flex)`
    justify-content: end;
`;


export const FlexSpaceBetween = styled(Flex)`
    justify-content: space-between;
    align-items: center; //all inheritees use this
`;

export const FlexJustifyCenter = styled(Flex)`
    justify-content: center;
`;

export const FlexStart = styled(Flex)`
    justify-content: start;
`;

export const FlexStartAlignCenter = styled(FlexStart)`
    align-items: center;
`;

export const FlexSm = styled(Flex)`
    gap: ${sizes.spacingSm};
`;

export const FlexSmAlignCenter = styled(FlexSm)`
    align-items: center;
`;

export const FlexMd = styled(Flex)`
    gap: ${sizes.spacingMd};
`;

export const FlexEndSm = styled(FlexEnd)`
    gap: ${sizes.spacingSm};
`;

export const FlexEndMd = styled(FlexEnd)`
    gap: ${sizes.spacingMd};
`;

export const FlexLg = styled(Flex)`
    gap: ${sizes.spacingLg};
`;

export const FlexLgAlignCenter = styled(FlexLg)`
    align-items: center;
`;

export const FlexAlignCenter = styled(Flex)`
    align-items: center;
`;

export const FlexMdAlignCenter = styled(FlexMd)`
    align-items: center;
`;

export const FlexCenter = styled(Flex)`
    align-items: center;
    justify-content: center;
`;

//FLEX COLUMNS
export const FlexCol = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

export const FlexColXs = styled(FlexCol)`
    gap: ${sizes.spacingXs};
`;

export const FlexColSm = styled(FlexCol)`
    gap: ${sizes.spacingSm};
`;

export const FlexColMd = styled(FlexCol)`
    gap: ${sizes.spacingMd};
`;

export const FlexColFitContent = styled(FlexCol)`
    width: fit-content;
`;

export const FlexColMdFitContent = styled(FlexColFitContent)`
    gap: ${sizes.spacingMd};
`;

export const FlexColRg = styled(FlexCol)`
    gap: ${sizes.spacingRg};
`;

export const FlexColLg = styled(FlexCol)`
    gap: ${sizes.spacingLg};
`;

export const FlexColAlignCenter = styled(FlexCol)`
    align-items: center;
`;

export const FlexColCenter = styled(FlexCol)`
    align-items: center;
    justify-content: center;
`;

// ---- Link to wrap logos

export const LinkWrapper = styled.a`
    margin: 0;
    padding: 0;
    height: auto;
    width: auto;
`;

export const HorizontalRule = styled.hr`
    border-top: 1px solid ${theme.cardBorder};
    margin: ${sizes.spacingXs} 0;
`;

export const ButtonRowLeft = styled(FlexStartAlignCenter)`
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
    }
    gap: ${sizes.spacingMd};
`;

export const ButtonRowRight = styled(FlexEnd)`
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
    }
    align-items: center;
    gap: ${sizes.spacingMd};
    width: 100%;
`;
